
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getFeaturedListings } from '../../../services/listings';
import type { Listing } from '../../../services/listings';
import { useFavorites } from '../../../hooks/useFavorites';

const FeaturedListings = () => {
  const [featuredItems, setFeaturedItems] = useState<Array<Listing & {
    originalPrice: number;
    seller?: string;
    rating?: number;
    location?: string;
    distance?: string;
    condition?: string;
    verified?: boolean;
    timePosted?: string;
    category?: string;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites();

  useEffect(() => {
    (async () => {
      try {
        const items = await getFeaturedListings();
        const normalized = items.map(i => ({
          ...i,
          originalPrice: i.originalPrice ?? Math.round(i.price * 1.1),
          seller: i.seller ?? 'Verified Seller',
          rating: i.rating ?? 4.8,
          location: i.location ?? 'Campus',
          distance: i.distance ?? '—',
          condition: i.condition ?? 'Good',
          verified: i.verified ?? false,
          timePosted: i.timePosted ?? 'recently',
          category: i.category ?? 'General',
        }));
        // Show only 5 as requested
        setFeaturedItems(normalized.slice(0, 5));
      } catch (e) {
        setFeaturedItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);
    /*
    { id: 1,
      title: 'MacBook Pro 13" M1 2021',
      price: 450000,
      originalPrice: 650000,
      seller: 'Sarah Chen',
      rating: 4.9,
      location: 'West Campus',
      distance: '0.3 miles',
      image: 'https://readdy.ai/api/search-image?query=MacBook%20Pro%2013%20inch%20M1%202021%20silver%20laptop%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20minimalist%20setup&width=400&height=300&seq=macbook-listing&orientation=landscape',
      condition: 'Excellent',
      verified: true,
      timePosted: '2 hours ago',
      category: 'Electronics'
    },
    {
      id: 2,
      title: 'Calculus Early Transcendentals 8th Ed',
      price: 22500,
      originalPrice: 160000,
      seller: 'Mike Rodriguez',
      rating: 4.8,
      location: 'East Dorms',
      distance: '0.5 miles',
      image: 'https://readdy.ai/api/search-image?query=Calculus%20textbook%20Early%20Transcendentals%208th%20edition%20mathematics%20book%20on%20clean%20white%20background%2C%20academic%20textbook%20photography&width=400&height=300&seq=calculus-book&orientation=landscape',
      condition: 'Good',
      verified: true,
      timePosted: '5 hours ago',
      category: 'Textbooks'
    },
    {
      id: 3,
      title: 'IKEA Desk Chair - Ergonomic',
      price: 17500,
      originalPrice: 44500,
      seller: 'Emma Wilson',
      rating: 4.7,
      location: 'North Campus',
      distance: '0.8 miles',
      image: 'https://readdy.ai/api/search-image?query=IKEA%20ergonomic%20desk%20chair%20black%20modern%20office%20chair%20on%20clean%20white%20background%2C%20furniture%20photography%2C%20minimalist%20style&width=400&height=300&seq=desk-chair&orientation=landscape',
      condition: 'Very Good',
      verified: false,
      timePosted: '1 day ago',
      category: 'Furniture'
    },
    {
      id: 4,
      title: 'iPhone 13 Pro 128GB Graphite',
      price: 325000,
      originalPrice: 499500,
      seller: 'Alex Thompson',
      rating: 5.0,
      location: 'Central Campus',
      distance: '0.2 miles',
      image: 'https://readdy.ai/api/search-image?query=iPhone%2013%20Pro%20graphite%20black%20smartphone%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%20mobile%20device&width=400&height=300&seq=iphone-13-pro&orientation=landscape',
      condition: 'Like New',
      verified: true,
      timePosted: '3 hours ago',
      category: 'Electronics'
    },
    {
      id: 5,
      title: 'Organic Chemistry Textbook Bundle',
      price: 42500,
      originalPrice: 225000,
      seller: 'Jessica Park',
      rating: 4.9,
      location: 'Science Building',
      distance: '0.4 miles',
      image: 'https://readdy.ai/api/search-image?query=Organic%20chemistry%20textbook%20bundle%20multiple%20books%20stacked%20on%20clean%20white%20background%2C%20academic%20science%20books%20photography&width=400&height=300&seq=chemistry-books&orientation=landscape',
      condition: 'Good',
      verified: true,
      timePosted: '6 hours ago',
      category: 'Textbooks'
    },
    {
      id: 6,
      title: 'Mini Fridge - Perfect for Dorms',
      price: 60000,
      originalPrice: 99500,
      seller: 'David Kim',
      rating: 4.6,
      location: 'South Campus',
      distance: '1.2 miles',
      image: 'https://readdy.ai/api/search-image?query=Mini%20refrigerator%20compact%20dorm%20fridge%20white%20appliance%20on%20clean%20white%20background%2C%20small%20appliance%20photography&width=400&height=300&seq=mini-fridge&orientation=landscape',
      condition: 'Excellent',
      verified: true,
      timePosted: '4 hours ago',
      category: 'Appliances'
    }
  ];
*/

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <section className="py-8 md:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 md:mb-12">
          <div className="mb-4 md:mb-0">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-orange-600 mb-3">
              <i className="ri-fire-fill"></i>
              Trending
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 md:mb-4">
              Trending Now
            </h2>
            <p className="text-lg md:text-xl text-gray-600">
              The hottest deals students are grabbing right now
            </p>
          </div>
          
          <Link 
            to="/marketplace"
            className="hidden md:inline-flex items-center px-6 py-3 border-2 border-teal-600 text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            View All
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-6">
          {loading && (
            Array.from({ length: 5 }).map((_, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-pulse">
                <div className="w-full h-20 md:h-24 bg-gray-200" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                  <div className="h-3 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ))
          )}
           {featuredItems.map((item) => (
             <Link
               key={item.id}
               to={`/product/${item.id}`}
               className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-teal-200 overflow-hidden cursor-pointer"
             >
              {/* Image */}
              <div className="relative overflow-hidden">
                <img
                  src={item.images?.[0] || item.image || ''}
                  alt={item.title}
                  className="w-full h-20 md:h-24 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-3 left-3">
                  <span className="bg-teal-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                    {item.category}
                  </span>
                </div>
                <div className="absolute top-2 right-2">
                  {(() => { const fav = isFavorite(item.id); return (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation();
                        fav
                          ? removeFromFavorites(item.id)
                          : addToFavorites({ id: item.id, name: item.title, price: formatPrice(item.price), image: item.images?.[0] || item.image || '' });
                      }}
                      aria-label={fav ? 'Remove from favorites' : 'Add to favorites'}
                      className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                    >
                      <i className={`${fav ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-gray-600 hover:text-red-500'} text-sm`}></i>
                    </button>
                  ); })()}
                </div>
                {item.verified && (
                  <div className="absolute bottom-3 left-3">
                    <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center">
                      <i className="ri-shield-check-fill mr-1"></i>
                      Verified
                    </span>
                  </div>
                )}
              </div>
              
              {/* Content */}
              <div className="p-3">
                <h3 className="font-semibold text-gray-900 mb-1.5 group-hover:text-teal-600 transition-colors line-clamp-2 text-sm md:text-base">
                  {item.title}
                </h3>

                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-2">
                  <span className="text-base md:text-xl font-bold text-gray-900">{formatPrice(item.price)}</span>
                  <span className="text-xs md:text-sm text-gray-500 line-through">{formatPrice(item.originalPrice)}</span>
                  <span className="text-xs md:text-sm text-green-600 font-medium">
                    {Math.round((1 - item.price / item.originalPrice) * 100)}% off
                  </span>
                </div>

                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 md:w-6 md:h-6 bg-gray-300 rounded-full"></div>
                    <span className="text-xs md:text-sm font-medium text-gray-700">{item.seller}</span>
                    <div className="flex items-center space-x-1">
                      <i className="ri-star-fill text-yellow-400 text-xs"></i>
                      <span className="text-xs text-gray-600">{item.rating}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs md:text-sm text-gray-500 mb-2">
                  <div className="flex items-center space-x-1">
                    <i className="ri-map-pin-line text-xs"></i>
                    <span>{item.location}</span>
                    <span>•</span>
                    <span>{item.distance}</span>
                  </div>
                  <span>{item.timePosted}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-600">
                    Condition: <span className="font-medium">{item.condition}</span>
                  </span>
                  <span
                    className="text-xs md:text-sm text-teal-600 font-semibold hover:text-teal-700"
                  >
                    View Details <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {!loading && featuredItems.length === 0 && (
          <div className="mt-6 md:mt-10 text-center">
            <p className="text-gray-600 mb-4">No featured items yet. Browse the marketplace for great deals.</p>
            <Link 
              to="/marketplace"
              className="inline-flex items-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors"
            >
              Explore Marketplace <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

export default FeaturedListings;
